// Tipos e helpers da aba "Equipe do dia" (painel do evento).
// Espelham o retorno das funções do banco — nenhuma coluna nem tabela nova.

export type TipoNotificar = 'ok' | 'erro' | 'aviso';
export type Notificar = (msg: string, tipo?: TipoNotificar) => void;

type Bruto = Record<string, unknown>;

function texto(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
}

function numero(v: unknown, padrao = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : padrao;
}

export interface OperadorLinha {
  operador_id: number;
  ponto_id: number | null;
  ponto_nome: string | null;
  nome: string;
  email: string;
  ja_entrou: boolean;
  caixa: boolean;
}

export interface PontoEquipe {
  ponto_id: number;
  nome: string;
  ativo: boolean;
}

export interface CaixaEquipe {
  caixa: number;
  nome: string;
  terminal_apelido: string | null;
  imprime_fichas: boolean;
  ativo: boolean;
}

export interface PortariaLinha {
  portaria_id: number;
  usuario_id: string;
  nome: string;
  email: string;
  desde: string | null;
}

export type SituacaoConvite = 'aguardando' | 'aceito' | 'vencido' | 'cancelado';

export interface ConviteLinha {
  convite_id: number;
  nome: string;
  telefone: string | null;
  funcoes: string;
  situacao: SituacaoConvite;
  link: string;
  criado_em: string | null;
  expira_em: string | null;
  aceito_em: string | null;
  aceito_por_nome: string | null;
}

// Linha de `equipe_quem_e_quem`: quem é cada pessoa, em que função e como está agora.
export interface QuemLinha {
  convite_id: number;
  pessoa: string;
  telefone: string | null;
  funcao: string;
  e_caixa: boolean;
  caixa_id: number | null;
  maquininha: string | null;
  situacao: string;
  como: string | null;
  no_celular_desde: string | null;
  ultima_abertura: string | null;
  aberturas: number;
  liberado_ate: string | null;
  link: string;
}

export interface MembroDirecao {
  id: number;
  pessoa: string | null;
  email: string | null;
  funcao: string | null;
  status: string;
}

// ————————————————— normalização —————————————————

export function normalizarOperador(b: Bruto): OperadorLinha {
  return {
    operador_id: numero(b.operador_id ?? b.id),
    ponto_id: b.ponto_id == null ? null : numero(b.ponto_id),
    ponto_nome: texto(b.ponto_nome),
    nome: texto(b.nome) ?? '',
    email: texto(b.email) ?? '',
    ja_entrou: b.ja_entrou === true,
    caixa: b.caixa === true,
  };
}

export function normalizarPonto(b: Bruto): PontoEquipe {
  return {
    ponto_id: numero(b.ponto_id ?? b.id),
    nome: texto(b.nome) ?? 'Barraca',
    ativo: b.ativo !== false,
  };
}

export function normalizarCaixa(b: Bruto): CaixaEquipe {
  return {
    caixa: numero(b.caixa ?? b.caixa_id ?? b.id),
    nome: texto(b.nome) ?? 'Caixa',
    terminal_apelido: texto(b.terminal_apelido),
    imprime_fichas: b.imprime_fichas === true,
    ativo: b.ativo !== false,
  };
}

// "Caixa 02 · Point · 4683" — nome + maquininha quando houver.
export function rotuloCaixaOpcao(c: CaixaEquipe): string {
  return c.terminal_apelido ? `${c.nome} · ${c.terminal_apelido}` : c.nome;
}

export function normalizarPortaria(b: Bruto): PortariaLinha {
  return {
    portaria_id: numero(b.portaria_id ?? b.id),
    usuario_id: String(b.usuario_id ?? ''),
    nome: texto(b.nome) ?? '',
    email: texto(b.email) ?? '',
    desde: texto(b.desde),
  };
}

// `funcoes` pode chegar como texto já pronto ("Caixa · Barraca X") ou como lista.
export function funcTexto(v: unknown): string {
  if (Array.isArray(v)) {
    return v.map((x) => String(x)).filter(Boolean).join(' · ');
  }
  return texto(v) ?? '';
}

function normalizarSituacao(v: unknown): SituacaoConvite {
  const s = texto(v)?.toLowerCase();
  if (s === 'aceito' || s === 'vencido' || s === 'cancelado') return s;
  return 'aguardando';
}

export function normalizarConvite(b: Bruto): ConviteLinha {
  return {
    convite_id: numero(b.convite_id ?? b.id),
    nome: texto(b.nome) ?? '',
    telefone: texto(b.telefone),
    funcoes: funcTexto(b.funcoes),
    situacao: normalizarSituacao(b.situacao),
    link: texto(b.link) ?? '',
    criado_em: texto(b.criado_em),
    expira_em: texto(b.expira_em),
    aceito_em: texto(b.aceito_em),
    aceito_por_nome: texto(b.aceito_por_nome),
  };
}

export function normalizarQuem(b: Bruto): QuemLinha {
  return {
    convite_id: numero(b.convite_id ?? b.id),
    pessoa: texto(b.pessoa) ?? '',
    telefone: texto(b.telefone),
    funcao: texto(b.funcao) ?? '',
    e_caixa: b.e_caixa === true,
    caixa_id: b.caixa_id == null ? null : numero(b.caixa_id),
    maquininha: texto(b.maquininha),
    situacao: (texto(b.situacao) ?? '').toLowerCase(),
    como: texto(b.como),
    no_celular_desde: texto(b.no_celular_desde),
    ultima_abertura: texto(b.ultima_abertura),
    aberturas: numero(b.aberturas),
    liberado_ate: texto(b.liberado_ate),
    link: texto(b.link) ?? '',
  };
}

export function normalizarMembro(b: Bruto): MembroDirecao {
  return {
    id: numero(b.id),
    pessoa: texto(b.pessoa),
    email: texto(b.email),
    funcao: texto(b.funcao),
    status: texto(b.status) ?? '',
  };
}

// ————————————————— avatares e telefone —————————————————

export function iniciais(nome: string, email = ''): string {
  const base = (nome || '').trim() || (email || '').trim();
  const partes = base.split(/[\s@._-]+/).filter(Boolean);
  if (partes.length === 0) return '?';
  const primeira = partes[0].charAt(0);
  const segunda = partes.length > 1 ? partes[1].charAt(0) : '';
  return (primeira + segunda).toUpperCase();
}

export function apenasDigitos(s: string | null | undefined): string {
  return (s ?? '').replace(/\D/g, '');
}

// Máscara (14) 9 9999-9999 enquanto digita.
export function mascararTelefone(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (!d) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 3) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2, 3)} ${d.slice(3)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 3)} ${d.slice(3, 7)}-${d.slice(7)}`;
}

export function formatarTelefone(v: string | null | undefined): string {
  const d = apenasDigitos(v);
  if (d.length === 11) return mascararTelefone(d);
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return v ?? '';
}

// Link do wa.me: completa com 55 quando o número é brasileiro sem DDI.
export function linkWhatsApp(numero: string | null | undefined, texto: string): string {
  const d = apenasDigitos(numero);
  const textoCod = encodeURIComponent(texto);
  if (!d) return `https://wa.me/?text=${textoCod}`;
  const completo = d.startsWith('55') && d.length >= 12 && d.length <= 13 ? d : `55${d}`;
  return `https://wa.me/${completo}?text=${textoCod}`;
}

// Texto pronto para reenviar um convite que já existe.
export function mensagemConviteExistente(p: {
  nome: string;
  evento: string;
  funcoes: string;
  link: string;
}): string {
  const primeiro = (p.nome || '').trim().split(/\s+/)[0] || 'pessoal';
  return (
    `Olá, ${primeiro}! Você foi chamado para servir no *${p.evento}* como: *${p.funcoes}*. ` +
    `Toque no link e a sua tela abre sozinha (não precisa criar conta): ${p.link}`
  );
}

// Compatibilidade: a função do banco ainda devolve a frase antiga. Reescreve para a tela.
export function ajustarMensagemConvite(mensagem: string): string {
  return mensagem.replace(
    'Toque no link, entre com a sua conta e aceite:',
    'Toque no link e a sua tela abre sozinha (não precisa criar conta):',
  );
}

// ————————————————— datas (horário de Brasília) —————————————————

const FUSO = 'America/Sao_Paulo';

// Início do dia de hoje em Brasília, em ISO (UTC).
export function inicioDoDiaBrasiliaISO(): string {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSO,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const pega = (t: string) => partes.find((p) => p.type === t)?.value ?? '01';
  const iso = `${pega('year')}-${pega('month')}-${pega('day')}T00:00:00-03:00`;
  return new Date(iso).toISOString();
}

export function horaBrasilia(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO,
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

// "dd/mm HH:MM" no horário de Brasília.
export function dataHoraBrasilia(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const data = new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO,
    day: '2-digit',
    month: '2-digit',
  }).format(d);
  return `${data} ${horaBrasilia(iso)}`;
}

// Só "dd/mm".
export function dataCurtaBrasilia(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO,
    day: '2-digit',
    month: '2-digit',
  }).format(d);
}

// A data está no futuro? (usado para "Liberado até HH:MM").
export function noFuturo(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && d.getTime() > Date.now();
}

export function formatarDinheiro(centavos: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(centavos ?? 0) / 100,
  );
}

// ————————————————— Vendas —————————————————

export interface VendasMeio {
  vendas: number;
  total_centavos: number;
}

export interface VendasResumo {
  caixa_vendas: number;
  caixa_total_centavos: number;
  online_vendas: number;
  online_total_centavos: number;
  fichas_vendidas: number;
  fichas_entregues_na_barraca: number;
  desfeitas: number;
  primeira_em: string | null;
  ultima_em: string | null;
}

export interface VendasHora {
  hora: string;
  vendas: number;
  total_centavos: number;
}

export interface VendasPessoa {
  nome: string;
  caixas: number;
  vendas: number;
  total_centavos: number;
  dinheiro_centavos: number;
  pix_centavos: number;
  cartao_centavos: number;
  primeira_em: string | null;
  ultima_em: string | null;
  desfeitas: number;
}

export interface VendasBarraca {
  barraca: string;
  fichas_entregues: number;
  por_pessoa: { nome: string; fichas: number; ultima_em: string | null }[];
}

export interface VendasDesfeita {
  numero: number | string;
  total_centavos: number;
  em: string | null;
  vendeu: string | null;
  motivo: string | null;
}

export interface VendasDados {
  resumo: VendasResumo;
  por_meio: {
    dinheiro?: VendasMeio;
    pix?: VendasMeio;
    cartao?: VendasMeio;
    outro?: VendasMeio;
  };
  por_hora: VendasHora[];
  caixa_por_pessoa: VendasPessoa[];
  barracas: VendasBarraca[];
  desfeitas: VendasDesfeita[];
}

const MEIO_ZERO: VendasMeio = { vendas: 0, total_centavos: 0 };

export function normalizarVendas(v: unknown): VendasDados | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const resumo = (o.resumo as Record<string, unknown>) ?? {};
  return {
    resumo: {
      caixa_vendas: numero(resumo.caixa_vendas),
      caixa_total_centavos: numero(resumo.caixa_total_centavos),
      online_vendas: numero(resumo.online_vendas),
      online_total_centavos: numero(resumo.online_total_centavos),
      fichas_vendidas: numero(resumo.fichas_vendidas),
      fichas_entregues_na_barraca: numero(resumo.fichas_entregues_na_barraca),
      desfeitas: numero(resumo.desfeitas),
      primeira_em: texto(resumo.primeira_em),
      ultima_em: texto(resumo.ultima_em),
    },
    por_meio: {
      dinheiro: (o.por_meio as Record<string, VendasMeio>)?.dinheiro ?? MEIO_ZERO,
      pix: (o.por_meio as Record<string, VendasMeio>)?.pix ?? MEIO_ZERO,
      cartao: (o.por_meio as Record<string, VendasMeio>)?.cartao ?? MEIO_ZERO,
      outro: (o.por_meio as Record<string, VendasMeio>)?.outro ?? MEIO_ZERO,
    },
    por_hora: Array.isArray(o.por_hora) ? (o.por_hora as VendasHora[]) : [],
    caixa_por_pessoa: Array.isArray(o.caixa_por_pessoa)
      ? (o.caixa_por_pessoa as VendasPessoa[])
      : [],
    barracas: Array.isArray(o.barracas) ? (o.barracas as VendasBarraca[]) : [],
    desfeitas: Array.isArray(o.desfeitas) ? (o.desfeitas as VendasDesfeita[]) : [],
  };
}